import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardBody,
  Flex,
  Icon,
  Text,
  CardHeader,
  CardFooter,
  Wrap,
  WrapItem,
  AccordionProvider,
  Accordion,
  AccordionItem,
  AccordionButton,
  Badge,
  AccordionPanel,
  Button as ChakraButton,
  useDisclosure,
  ModalHeader,
  Modal as ChakraModal,
  ModalOverlay,
  ModalCloseButton,
  ModalContent,
  ModalFooter as ChakraModalFooter,
  FormLabel,
  Input,
  FormControl,
  ModalBody as ChakraModalBody,
  Textarea,
  useUpdateEffect,
  Select,
  Heading,
} from '@chakra-ui/react';
import { FaTrash, FaPlus, FaTasks } from 'react-icons/fa';
import TaskTable from './taskTable';
import { SelectPicker } from 'rsuite';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { useFeatureFlagFromContext } from '../../../launchdarkly';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  useNexus,
  showToast,
} from '@spotoninc/nexus-react';
import { AddIcon } from '@spotoninc/nexus-web-assets';
import '../../../styles/reskinned-task-manager.css';
import ImprovedTaskManager from './ImprovedTaskManager';

// Reskinned Task Manager component using Nexus components
const ReskinnedTaskManager = () => {
  useNexus(); // Initialize Nexus context
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Function to process task values in QA environment
  const processTaskValue = value => {
    // Check if we're in QA environment
    if (
      process.env.REACT_APP_ENV === 'qa' &&
      value &&
      typeof value === 'string'
    ) {
      // Remove the word 'balance' (case insensitive) from the task value
      return value.replace(/balance\s*/i, '');
    }
    return value;
  };

  const fetchUsers = useCallback(() => {
    setUsers([]);
    setLoading(true);

    fetchWithAuth('/api/get_users/', {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        const dd_data = Array.isArray(data['data'])
          ? data['data'].map(item => ({
              label: item.first_name,
              value: item.user_id_id,
            }))
          : [];
        if (Array.isArray(dd_data)) {
          setUsers(dd_data);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const fetchTasks = useCallback((sortType = 'asc') => {
    const formDat = new FormData();
    formDat.append('type', 'created');
    formDat.append('sortType', sortType);

    fetchWithAuth('/api/get_tasks/', {
      method: 'POST',
      body: formDat,
    })
      .then(response => response.json())
      .then(data => {
        const tasksData = data['data'].map(item => ({
          id: item.id,
          ownerName: item.first_name,
          lastName: item.last_name,
          firstName: item.first_name,
          dueDate: item.due_on,
          header: item.task_title,
          status: item.status,
          description: item.task_desc,
        }));
        setTasks(tasksData);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const handleDelete = useCallback(
    id => {
      const taskData = new FormData();
      taskData.append('taskId', id);
      taskData.append('action', 'status_update');
      taskData.append('status', 'Cancelled');

      fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(() => {
          fetchTasks();
        })
        .catch(err => console.error(err));
    },
    [fetchTasks]
  );

  const addTask = useCallback(() => {
    setLoading(true);

    const taskName = document.getElementById('nexus-taskName').value;
    const taskDesc = document.getElementById('nexus-desc').value;
    const ownerId = document.getElementById('nexus-owner').value;
    const dueDate = document.getElementById('nexus-dueOn').value;

    const taskData = new FormData();
    taskData.append('task_title', taskName);
    taskData.append('task_desc', taskDesc);
    taskData.append('assigned_to', ownerId);
    taskData.append('status', 'Not yet Started');
    taskData.append('due_on', dueDate);
    taskData.append('taskId', '');

    fetchWithAuth('/api/modify_task/', {
      method: 'POST',
      body: taskData,
    })
      .then(response => response.json())
      .then(() => {
        fetchTasks();
        setIsModalOpen(false);

        // Show success toast notification
        showToast({
          priority: 'success',
          content: 'Task added successfully',
          autoClose: 6000,
        });
      })
      .catch(err => {
        console.error(err);

        // Show error toast notification
        showToast({
          priority: 'error',
          content: 'Failed to add task: ' + (err.message || 'Unknown error'),
          autoClose: 6000,
        });
      });
  }, [fetchTasks]);

  const modifyTask = useCallback(() => {
    if (!selectedTask) return;

    setLoading(true);

    const taskName = document.getElementById('nexus-edit-taskName').value;
    const taskDesc = document.getElementById('nexus-edit-desc').value;
    const ownerId = document.getElementById('nexus-edit-owner').value;
    const dueDate = document.getElementById('nexus-edit-dueOn').value;

    const taskData = new FormData();
    taskData.append('task_title', taskName);
    taskData.append('task_desc', taskDesc);
    taskData.append('assigned_to', ownerId);
    taskData.append('status', 'Not yet Started');
    taskData.append('due_on', dueDate);
    taskData.append('taskId', selectedTask.id);
    taskData.append('action', 'modify');

    fetchWithAuth('/api/modify_task/', {
      method: 'POST',
      body: taskData,
    })
      .then(response => response.json())
      .then(() => {
        fetchTasks();
        setIsEditModalOpen(false);
        setSelectedTask(null);

        // Show success toast notification
        showToast({
          priority: 'success',
          content: 'Task updated successfully',
          autoClose: 6000,
        });
      })
      .catch(err => {
        console.error(err);

        // Show error toast notification
        showToast({
          priority: 'error',
          content: 'Failed to update task: ' + (err.message || 'Unknown error'),
          autoClose: 6000,
        });
      });
  }, [fetchTasks, selectedTask]);

  // Similar to componentDidMount
  useEffect(() => {
    setLoading(true);
    fetchUsers();
    fetchTasks();
  }, [fetchUsers, fetchTasks]);

  return (
    <div className="nexus-task-manager">
      <div className="nexus-task-page-header">
        <div className="nexus-task-title">
          {/* Hide the icon when showReskin is true */}
          {/* <ListViewIcon /> */}
          <Heading size={'lg'} className="reskinned-task-title">
            Tasks
          </Heading>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          leadingIcon={function Xs() {
            return <AddIcon size={'1.5rem'} />;
          }}
        >
          Add Task
        </Button>
      </div>

      <TaskTable
        data={tasks}
        users={users}
        handleDel={handleDelete}
        modify={modifyTask}
        setLoading={setLoading}
        loading={loading}
        retrieveData={fetchTasks}
      />

      {/* Add Task Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add Task"
        className="nexus-task-modal"
      >
        <ModalBody>
          <div className="nexus-form-group">
            <label htmlFor="nexus-taskName">Task name</label>
            <input
              type="text"
              id="nexus-taskName"
              placeholder="Task name"
              required
            />
          </div>

          <div className="nexus-form-row">
            <div className="nexus-form-group">
              <label htmlFor="nexus-owner">Assigned To</label>
              <select id="nexus-owner">
                {users.map((user, index) => (
                  <option key={index} value={user.value}>
                    {user.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="nexus-form-group">
              <label htmlFor="nexus-dueOn">Due Date</label>
              <input type="date" id="nexus-dueOn" required />
            </div>
          </div>

          <div className="nexus-form-group">
            <label htmlFor="nexus-desc">Description</label>
            <textarea
              id="nexus-desc"
              placeholder="Description"
              required
            ></textarea>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="tonal" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={addTask}>Save</Button>
        </ModalFooter>
      </Modal>

      {/* Edit Task Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTask(null);
        }}
        title="Edit Task"
        className="nexus-task-modal"
      >
        <ModalBody>
          {selectedTask && (
            <>
              <div className="nexus-form-group">
                <label htmlFor="nexus-edit-taskName">Task name</label>
                <input
                  type="text"
                  id="nexus-edit-taskName"
                  placeholder="Task name"
                  defaultValue={processTaskValue(selectedTask.header)}
                  required
                />
              </div>

              <div className="nexus-form-row">
                <div className="nexus-form-group">
                  <label htmlFor="nexus-edit-owner">Assigned To</label>
                  <select
                    id="nexus-edit-owner"
                    defaultValue={
                      users.find(u => u.label === selectedTask.ownerName)?.value
                    }
                  >
                    {users.map((user, index) => (
                      <option key={index} value={user.value}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="nexus-form-group">
                  <label htmlFor="nexus-edit-dueOn">Due Date</label>
                  <input
                    type="date"
                    id="nexus-edit-dueOn"
                    defaultValue={selectedTask.dueDate}
                    required
                  />
                </div>
              </div>

              <div className="nexus-form-group">
                <label htmlFor="nexus-edit-desc">Description</label>
                <textarea
                  id="nexus-edit-desc"
                  placeholder="Description"
                  defaultValue={processTaskValue(selectedTask.description)}
                  required
                ></textarea>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="tonal"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedTask(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={modifyTask}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// Original Task Manager component
const TaskManager = ({ showReskin }) => {
  const [MisOpen, setMisOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [cardBodyOverflow, setCardBodyOverflow] = useState('auto');

  const fetchUsers = useCallback(() => {
    setUsers([]);
    setTableLoading(true);

    fetchWithAuth('/api/get_users/', {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        const dd_data = Array.isArray(data['data'])
          ? data['data'].map(item => ({
              label: item.first_name,
              value: item.user_id_id,
            }))
          : [];
        if (Array.isArray(dd_data)) {
          setUsers(dd_data);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const fetchTasks = useCallback(sortType => {
    const formDat = new FormData();
    formDat.append('type', 'created');
    formDat.append('sortType', sortType);

    fetchWithAuth('/api/get_tasks/', {
      method: 'POST',
      body: formDat,
    })
      .then(response => response.json())
      .then(data => {
        const tasksData = data['data'].map(item => ({
          id: item.id,
          ownerName: item.first_name,
          lastName: item.last_name,
          firstName: item.first_name,
          dueDate: item.due_on,
          header: item.task_title,
          status: item.status,
          description: item.task_desc,
        }));
        setTasks(tasksData);
        setTableLoading(false);
        checkCardBodyOverflow();
      })
      .catch(err => console.error(err));
  }, []);

  const checkCardBodyOverflow = useCallback(() => {
    const cardBodyElement = document.getElementById('cardBody');
    const contentElement = document.getElementById('content');

    if (cardBodyElement && contentElement) {
      const cardBodyHeight = cardBodyElement.clientHeight;
      const contentHeight = contentElement.scrollHeight;

      if (contentHeight > cardBodyHeight) {
        setCardBodyOverflow('scroll');
      } else {
        setCardBodyOverflow('auto');
      }
    }
  }, []);

  const handelDelete = useCallback(
    id => {
      const taskData = new FormData();
      taskData.append('taskId', id);
      taskData.append('action', 'status_update');
      taskData.append('status', 'Cancelled');

      fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(() => {
          fetchTasks();

          // Show success toast notification
          showToast({
            priority: 'success',
            content: 'Task cancelled successfully',
            autoClose: 6000,
          });
        })
        .catch(err => {
          console.error(err);

          // Show error toast notification
          showToast({
            priority: 'error',
            content:
              'Failed to cancel task: ' + (err.message || 'Unknown error'),
            autoClose: 6000,
          });
        });
    },
    [fetchTasks]
  );

  const modifyTasks = useCallback(
    rowData => {
      setTableLoading(true);
      setTasks([]);

      const ownerId =
        document.getElementById('owner1').value ||
        users.find(item => item.label === rowData.ownerName)?.value;

      const taskData = new FormData();
      taskData.append('task_title', document.getElementById('taskName1').value);
      taskData.append('task_desc', document.getElementById('desc1').value);
      taskData.append('assigned_to', ownerId);
      taskData.append('status', 'Not yet Started');
      taskData.append('due_on', document.getElementById('dueOn1').value);
      taskData.append('taskId', rowData.id);
      taskData.append('action', 'modify');

      fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(data => {
          fetchTasks();

          // Show success toast notification
          showToast({
            priority: 'success',
            content: 'Task updated successfully',
            autoClose: 6000,
          });
        })
        .catch(err => {
          console.error(err);

          // Show error toast notification
          showToast({
            priority: 'error',
            content:
              'Failed to update task: ' + (err.message || 'Unknown error'),
            autoClose: 6000,
          });
        });
    },
    [fetchTasks, users]
  );

  const addTasks = useCallback(() => {
    setTableLoading(true);
    setTasks([]);

    const taskData = new FormData();
    taskData.append('task_title', document.getElementById('taskName').value);
    taskData.append('task_desc', document.getElementById('desc').value);
    taskData.append('assigned_to', document.getElementById('owner').value);
    taskData.append('status', 'Not yet Started');
    taskData.append('due_on', document.getElementById('dueOn').value);
    taskData.append('taskId', '');

    fetchWithAuth('/api/modify_task/', {
      method: 'POST',
      body: taskData,
    })
      .then(response => response.json())
      .then(() => {
        fetchTasks();

        // Show success toast notification
        showToast({
          priority: 'success',
          content: 'Task added successfully',
          autoClose: 6000,
        });
      })
      .catch(err => {
        console.error(err);

        // Show error toast notification
        showToast({
          priority: 'error',
          content: 'Failed to add task: ' + (err.message || 'Unknown error'),
          autoClose: 6000,
        });
      });

    setMisOpen(!MisOpen);
  }, [MisOpen, fetchTasks]);

  const setLoading = useCallback(val => {
    setTableLoading(val);
  }, []);

  // Similar to componentDidMount
  useEffect(() => {
    setTableLoading(true);
    fetchUsers();
    fetchTasks();
  }, [fetchUsers, fetchTasks]);

  // Render the component
  return (
    <Flex direction="column">
      <Card width={'100%'} flex="1" display="flex" flexDirection="column">
        <CardHeader fontSize={'sm'}>
          <Flex>
            <Flex gap={2} flex={1} alignItems={'center'}>
              <Icon as={FaTasks} />
              <Text>Tasks</Text>
            </Flex>
            <Flex flex={1} justifyContent={'flex-end'}>
              <ChakraButton
                fontSize={'sm'}
                onClick={() => {
                  setMisOpen(!MisOpen);
                  fetchTasks();
                }}
              >
                <Icon as={FaPlus} />
                <Text>Add Task</Text>
              </ChakraButton>
              <ChakraModal
                isOpen={MisOpen}
                onClose={() => {
                  setMisOpen(!MisOpen);
                }}
                size={{ sm: 'xl', md: '2xl', lg: '3xl' }}
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Add Task</ModalHeader>
                  <ModalCloseButton />
                  <ChakraModalBody>
                    <Flex
                      width={'100%'}
                      direction={'column'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      gap={4}
                    >
                      <FormControl>
                        <FormLabel fontSize={'sm'}>Task name</FormLabel>
                        <Input placeholder="Task name" id="taskName" required />
                      </FormControl>
                      <Flex gap={2} width={'100%'}>
                        <FormControl flex={1}>
                          <FormLabel fontSize={'sm'}>Assigned To</FormLabel>
                          <Select width="100%" id="owner">
                            {users !== undefined ? (
                              users.map((item, index) => (
                                <option key={index} value={item.value}>
                                  {item.label}
                                </option>
                              ))
                            ) : (
                              <></>
                            )}
                          </Select>
                        </FormControl>
                        <FormControl flex={1}>
                          <FormLabel fontSize={'sm'}>Due Date</FormLabel>
                          <Input
                            placeholder="Due Date"
                            type="date"
                            id="dueOn"
                            required
                          />
                        </FormControl>
                      </Flex>
                      <FormControl>
                        <FormLabel fontSize={'sm'}>Description</FormLabel>
                        <Textarea
                          style={{ whiteSpace: 'pre-line' }}
                          placeholder="Desription"
                          id="desc"
                          required
                        ></Textarea>
                      </FormControl>
                    </Flex>
                  </ChakraModalBody>

                  <ChakraModalFooter>
                    <ChakraButton colorScheme="blue" mr={3} onClick={addTasks}>
                      Save
                    </ChakraButton>
                    <ChakraButton
                      onClick={() => {
                        fetchTasks();
                        setMisOpen(!MisOpen);
                      }}
                    >
                      Cancel
                    </ChakraButton>
                  </ChakraModalFooter>
                </ModalContent>
              </ChakraModal>
            </Flex>
          </Flex>
        </CardHeader>
        <CardBody id="cardBody" overflowY={cardBodyOverflow} flex="1">
          <Flex direction="column" gap={2} id="content">
            <TaskTable
              data={tasks}
              users={users}
              handleDel={handelDelete}
              modify={modifyTasks}
              setLoading={setLoading}
              loading={tableLoading}
              retrieveData={fetchTasks}
            />
          </Flex>
        </CardBody>
      </Card>
    </Flex>
  );
};

// Main component that conditionally renders based on the feature flag
const TaskAdder = () => {
  const showReskin = useFeatureFlagFromContext('showReskin', false);
  const taskImprovements = useFeatureFlagFromContext('taskImprovements', false);

  if (!showReskin) {
    return <TaskManager showReskin={showReskin} />;
  }
  if (taskImprovements) {
    return <ImprovedTaskManager />;
  }
  return <ReskinnedTaskManager />;
};

export default TaskAdder;
